import { LocationSchema, ErrorMessage } from 'components/Form'
import { TextInput, CountryInput, StateInput } from 'components/UserInputs'
import { Formik, FormikProps } from 'formik'
import { useHandleEscapeKey } from 'hooks'
import { useEffect, useMemo } from 'react'
import { Box, Flex, Button } from 'theme-ui'
import {
  TeachingLocation,
  LocationFormikOnlyValues,
  UpdateLocationProps,
} from 'types/location'
import { lazy } from 'yup'
import { Loading } from 'components'
import { useUpdateLocation } from 'hooks/locationHooks/useUpdateLocation'
import { getChangedValues, hasChanged } from 'utils/helpers'
import { useSingleLocation } from 'hooks/locationHooks/useSingleLocation'
import { FormModal } from './FormModal'
import {
  googlePlacesPrefillFields,
  preFillFormWithPlace,
  validatePlaceData,
} from 'utils/googlePlaces'
import { PlacesAutocompleteInput } from 'components/Places/PlacesAutocompleteInput'
import { getFromLocalizedString } from 'utils/getFromLocalizedString'

type FormValues = UpdateLocationProps & LocationFormikOnlyValues

function getUpdatedLocationData({
  values,
  initialValues,
}: {
  values: FormValues
  initialValues: FormValues
}): UpdateLocationProps {
  validatePlaceData(values)
  const changedValues = getChangedValues<FormValues>({
    initialValues,
    values,
  })

  //no change in places relevant data so no need to change coordinates
  if (
    !values.__googleData &&
    !googlePlacesPrefillFields.some(
      (prefillField) => changedValues[prefillField] !== undefined
    )
  ) {
    delete changedValues.coordinates
  }

  delete changedValues.__country
  delete changedValues.__googleData

  return changedValues as UpdateLocationProps
}

export const LocationUpdateModal = ({
  id,
  onLocationUpdated,
  handleCancel,
  show,
}: {
  id: string
  onLocationUpdated: (location: TeachingLocation) => void
  handleCancel: () => void
  show: boolean
}) => {
  const [location] = useSingleLocation(id)
  const initialValues = useMemo(() => {
    return {
      ...location,
    } as FormValues
  }, [location])

  useHandleEscapeKey(handleCancel)

  const [update, updatedLocation, isLoading, error] = useUpdateLocation()
  useEffect(() => {
    if (updatedLocation) {
      onLocationUpdated(updatedLocation)
    }
  }, [updatedLocation, onLocationUpdated])

  const handleSubmit = (values: FormValues) => {
    const { id } = values
    const updatedLocation = getUpdatedLocationData({
      values,
      initialValues,
    })

    if (Object.entries(updatedLocation).length) {
      updatedLocation.id = id
      update(updatedLocation)
    }
  }
  return show && location ? (
    <FormModal
      heading="Edit address"
      SubHeading={
        <Box sx={{ pt: '5px' }}>
          <Box
            variant="text.error"
            sx={{ display: 'inline', lineHeight: '22px' }}
          >
            Warning!{' '}
          </Box>
          Editing this address will permanently change it in our national list.
          All teachers and students at this center will see the change.
        </Box>
      }
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={() => {
          return lazy(() => {
            const schema = LocationSchema
            return schema
          })
        }}
      >
        {({
          errors,
          values,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }: FormikProps<FormValues>) => {
          return (
            <form onSubmit={handleSubmit}>
              <Box
                data-cy="update-location-modal"
                variant="forms.locationForm"
                sx={{
                  '& > *': {
                    mt: '-6px',
                  },
                }}
              >
                <TextInput
                  name="name"
                  label="Center name"
                  value={getFromLocalizedString(values.name ?? '')}
                  disabled={
                    getFromLocalizedString(values.name ?? '') !==
                    (values.name ?? '')
                  }
                  error={touched.name ? errors.name : undefined}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={100}
                />
                <PlacesAutocompleteInput
                  name="addressLine"
                  label="Street address"
                  value={values.addressLine ?? ''}
                  countryCode={values.countryCode}
                  error={touched.addressLine ? errors.addressLine : undefined}
                  onPlaceSelected={(place) => {
                    preFillFormWithPlace(setFieldValue, place)
                  }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={101}
                />
                <TextInput
                  name="city"
                  label="City"
                  value={values.city}
                  error={touched.city ? errors.city : undefined}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={102}
                />
                <Flex
                  sx={{
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    '& > * ': {
                      flex: '0 0 150px',
                    },
                  }}
                >
                  <Box>
                    <StateInput
                      name="state"
                      label="State/Province"
                      countryCode={values.countryCode}
                      value={values.state}
                      error={touched.state ? errors.state : undefined}
                      setFieldValue={setFieldValue}
                      onBlur={handleBlur}
                      tabIndex={103}
                    />
                  </Box>
                  <Box>
                    <TextInput
                      name="zip"
                      label="Zip/Postal code"
                      value={values.zip}
                      error={touched.zip ? errors.zip : undefined}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      tabIndex={104}
                    />
                  </Box>
                </Flex>
                <CountryInput
                  label="Teaching country"
                  name="countryCode"
                  value={values.countryCode}
                  setFieldValue={setFieldValue}
                  error={touched.countryCode ? errors.countryCode : undefined}
                  onBlur={handleBlur}
                  tabIndex={105}
                />
                <TextInput
                  name="specialInstructions"
                  label="Special instructions"
                  placeholder="To help a student find this location"
                  value={getFromLocalizedString(
                    values.specialInstructions ?? ''
                  )}
                  disabled={
                    getFromLocalizedString(values.specialInstructions ?? '') !==
                    (values.specialInstructions ?? '')
                  }
                  error={
                    touched.specialInstructions
                      ? errors.specialInstructions
                      : undefined
                  }
                  onChange={handleChange}
                  onBlur={handleBlur}
                  tabIndex={106}
                />

                <Flex variant="forms.buttons.container" sx={{ mt: 20, mb: 5 }}>
                  <Button
                    type="submit"
                    variant="forms.buttons.primary"
                    disabled={
                      Object.keys(errors).length > 0 ||
                      isLoading ||
                      !hasChanged({ values, initialValues })
                    }
                  >
                    {isLoading ? <Loading /> : 'Save'}
                  </Button>
                  <Button
                    variant="forms.buttons.secondary"
                    type="button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Flex>
                <ErrorMessage sx={{ mb: 40 }}>
                  {error && <span>{error}</span>}
                </ErrorMessage>
              </Box>
            </form>
          )
        }}
      </Formik>
    </FormModal>
  ) : null
}
